const FOOTNOTE_DATA_ID_PREFIX = "seavien-footnote-";
const FOOTNOTE_NOTE_ID_SUFFIX = "note";
const FOOTNOTE_REFERENCE_ID_SUFFIX = "ref";
const FOOTNOTE_ORDINAL_ID_INFIX = "index-";
const FOOTNOTE_UNRESOLVED_ID = `${FOOTNOTE_DATA_ID_PREFIX}unresolved`;

const FOOTNOTE_ORDINAL_PATTERN = /^[0-9]+$/;
const FOOTNOTE_NOTE_ORDINAL_PATTERN = /^fn:([0-9]+)$/;

function getGlobalCryptoRandomUUID(): string | null {
    try {
        const candidate = globalThis.crypto as { randomUUID?: () => string } | undefined;
        return typeof candidate?.randomUUID === "function"
            ? candidate.randomUUID()
            : null;
    } catch {
        return null;
    }
}

function createFallbackRandomId(): string {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Mints a NEW footnote id. Only the command path may call this — the moment a user inserts a
 * footnote is the only moment a fresh identity is correct.
 *
 * Rendering must never call it. `renderHTML` runs server-side inside `TipTapParser.parse`
 * (`tiptap-json-parser` Lambda) over stored `source_json`, and a random draw there makes the parse
 * non-deterministic: the same document produces different stored HTML on every run, and the
 * reference half and the note half draw independently so `aria-controls` ends up pointing at an
 * element that exists nowhere in the output. Use `resolveFootnoteDataId` from a render path instead.
 */
export function createFootnoteDataId(): string {
    const randomId = getGlobalCryptoRandomUUID() ?? createFallbackRandomId();
    return `${FOOTNOTE_DATA_ID_PREFIX}${randomId}`;
}

/**
 * Pure. Returns the prefixed form of an authored footnote id, or `null` when there is nothing to
 * normalize. It deliberately does NOT invent an id — see `createFootnoteDataId` above.
 */
export function normalizeFootnoteDataId(value?: string | null): string | null {
    const trimmed = value?.trim();
    if (!trimmed) {
        return null;
    }

    return trimmed.startsWith(FOOTNOTE_DATA_ID_PREFIX)
        ? trimmed
        : `${FOOTNOTE_DATA_ID_PREFIX}${trimmed}`;
}

/**
 * The deterministic id a footnote falls back to when the document carries no usable `data-id`.
 *
 * The ordinal is the one `tiptap-footnotes` already maintains across both halves in a single loop:
 * the reference gets `referenceNumber` = 1..n in document order, and the matching note gets
 * `id: "fn:<n>"` from the same iteration. Reading that same number from each side is what makes the
 * two agree without a shared random draw. Anything that is not a plain non-negative integer is
 * refused rather than interpolated, since this value lands in a DOM id.
 */
export function getFootnoteDataIdForOrdinal(ordinal?: string | number | null): string {
    const trimmed = String(ordinal ?? "").trim();
    return FOOTNOTE_ORDINAL_PATTERN.test(trimmed)
        ? `${FOOTNOTE_DATA_ID_PREFIX}${FOOTNOTE_ORDINAL_ID_INFIX}${trimmed}`
        : FOOTNOTE_UNRESOLVED_ID;
}

/** Extracts the ordinal from a `tiptap-footnotes` note id (`"fn:3"` -> `"3"`). */
export function getFootnoteOrdinalFromNoteId(noteId?: unknown): string | null {
    return FOOTNOTE_NOTE_ORDINAL_PATTERN.exec(String(noteId ?? "").trim())?.[1] ?? null;
}

/**
 * The data id a render should emit: the authored one when present, otherwise the deterministic
 * ordinal-derived one. Both `renderHTML` bodies go through here so they cannot disagree.
 */
export function resolveFootnoteDataId(value?: unknown, ordinal?: string | number | null): string {
    const authored = typeof value === "string" ? normalizeFootnoteDataId(value) : null;
    return authored ?? getFootnoteDataIdForOrdinal(ordinal);
}

export function getFootnoteReferenceDomId(dataId?: string | null): string {
    return `${normalizeFootnoteDataId(dataId) ?? FOOTNOTE_UNRESOLVED_ID}-${FOOTNOTE_REFERENCE_ID_SUFFIX}`;
}

export function getFootnoteNoteDomId(dataId?: string | null): string {
    return `${normalizeFootnoteDataId(dataId) ?? FOOTNOTE_UNRESOLVED_ID}-${FOOTNOTE_NOTE_ID_SUFFIX}`;
}
